<template>
  <div class="lotes-cobrancas">
    <div v-if="!boletosOperacionais" class="alert alert-info" role="status">Operações de boletos desabilitadas para esta escola. O histórico permanece disponível.</div>
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h6 class="mb-1">
          Lotes de cobranças
        </h6>

        <small class="text-muted">
          Revise cobranças agrupadas por matrícula e distribua os links
          quando estiverem prontos.
        </small>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="loading || operacaoEmAndamento"
        @click="carregarLotes"
      >
        <i class="bi bi-arrow-clockwise me-1"></i>
        Atualizar
      </button>
    </div>

    <div class="card shadow-sm mb-3">
      <div class="card-body">
        <div class="lotes-filtros">
          <div class="lotes-filtro-busca">
            <label class="form-label">
              Busca
            </label>

            <input
              v-model.trim="filters.busca"
              type="search"
              class="form-control form-control-sm"
              placeholder="Aluno ou contrato"
            />
          </div>

          <div>
            <label class="form-label">
              Origem
            </label>

            <select
              v-model="filters.origem_evento"
              class="form-select form-select-sm"
              @change="aplicarFiltros"
            >
              <option value="">
                Todas
              </option>

              <option value="NOVA_MATRICULA">
                Nova matrícula
              </option>

              <option value="RENOVACAO_MATRICULA">
                Renovação
              </option>

              <option value="TROCA_PLANO">
                Troca de plano
              </option>
            </select>
          </div>

          <div class="d-flex align-items-end">
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="loading"
              @click="limparFiltros"
            >
              <i class="bi bi-funnel me-1"></i>
              Limpar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div class="card shadow-sm">
      <div class="card-body p-0">
        <div
          v-if="loading"
          class="py-5 text-center"
        >
          <div
            class="spinner-border spinner-border-sm text-primary"
            role="status"
          ></div>

          <div class="mt-2 text-muted">
            Carregando lotes...
          </div>
        </div>

        <div
          v-else-if="lotes.length === 0"
          class="py-5 text-center text-muted"
        >
          <i class="bi bi-collection fs-3 d-block mb-2"></i>

          Nenhum lote de cobranças encontrado.
        </div>

        <div
          v-else
          class="table-responsive"
        >
          <table class="table table-striped table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th>Lote</th>
                <th>Aluno / contrato</th>
                <th>Origem</th>
                <th>Situação do lote</th>
                <th>Progresso</th>
                <th>Criado em</th>
                <th class="text-center">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="lote in lotes"
                :key="lote.id"
              >
                <td class="fw-semibold">
                  #{{ lote.id }}
                </td>

                <td>
                  <div class="fw-semibold">
                    {{ lote.aluno_nome || 'Aluno não informado' }}
                  </div>

                  <small class="text-muted">
                    Contrato {{ lote.numero_contrato || '—' }}
                  </small>
                </td>

                <td>
                  {{ origemLabel(lote.origem_evento) }}
                </td>

                <td>
                  <span
                    class="lote-readiness"
                    :class="readinessClass(readinessEstado(lote))"
                  >
                    {{ readinessLabel(readinessEstado(lote)) }}
                  </span>
                </td>

                <td>
                  <div class="small">
                    <strong>
                      {{ numeroSeguro(lote.readiness?.total_prontas_envio) }}
                    </strong>
                    de
                    <strong>
                      {{ numeroSeguro(lote.quantidade_itens) }}
                    </strong>
                    pronta(s)
                  </div>

                  <small class="text-muted">
                    {{ numeroSeguro(lote.readiness?.total_com_boleto) }}
                    com boleto
                  </small>
                </td>

                <td>
                  {{ formatDateTime(lote.criado_em) }}
                </td>

                <td class="text-center">
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-1"
                    :disabled="operacaoEmAndamento"
                    title="Revisar lote"
                    @click="abrirLote(lote)"
                  >
                    <i class="bi bi-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
      <div>Mostrando {{ startRow }} a {{ endRow }} de {{ paginacao.total_itens }} lotes</div>
      <div class="d-flex align-items-center gap-2">
        <BFormSelect
          v-model="limitePorPagina"
          :options="pageOptions"
          :disabled="loading"
          size="sm"
          class="page-limit"
        />
      </div>
      <div class="d-flex align-items-center">
        <BPagination
          v-model="paginaAtual"
          :per-page="limitePorPagina"
          :total-rows="paginacao.total_itens"
          :disabled="loading"
          size="sm"
          limit="5"
          class="mb-0"
        />
      </div>
    </div>

    <div
      v-if="modalAberto"
      class="lote-modal-backdrop"
      @click.self="fecharLote"
    >
      <section
        class="lote-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lote-modal-title"
      >
        <header class="lote-modal-header">
          <div>
            <h5
              id="lote-modal-title"
              class="mb-1"
            >
              Lote #{{ loteSelecionado?.id || '—' }}
            </h5>

            <small class="text-muted">
              Contrato {{ loteSelecionado?.numero_contrato || '—' }}
            </small>
          </div>

          <button
            type="button"
            class="btn-close"
            :disabled="operacaoEmAndamento"
            aria-label="Fechar"
            @click="fecharLote"
          ></button>
        </header>

        <div class="lote-modal-body">
          <div
            v-if="carregandoDetalhe"
            class="py-5 text-center"
          >
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
            ></div>

            <div class="mt-2 text-muted">
              Carregando lote...
            </div>
          </div>

          <template v-else>
            <div
              v-if="actionErrorMessage"
              class="alert alert-danger"
              role="alert"
            >
              {{ actionErrorMessage }}
            </div>

            <div
              v-if="loteSelecionado"
              class="lote-resumo-grid mb-3"
            >
              <div>
                <span>Situação</span>
                <strong>
                  {{ readinessLabel(readinessEstado(loteSelecionado)) }}
                </strong>
              </div>

              <div>
                <span>Aluno</span>
                <strong>
                  {{ loteSelecionado.aluno_nome || '—' }}
                </strong>
              </div>

              <div>
                <span>Origem</span>
                <strong>
                  {{ origemLabel(loteSelecionado.origem_evento) }}
                </strong>
              </div>

              <div>
                <span>Prontas</span>
                <strong>
                  {{ numeroSeguro(loteSelecionado.readiness?.total_prontas_envio) }}
                  /
                  {{ numeroSeguro(loteSelecionado.quantidade_itens) }}
                </strong>
              </div>
            </div>

            <div
              v-if="readinessEstado(loteSelecionado) !== 'PRONTO_ENVIO'"
              class="alert alert-warning py-2"
            >
              Este lote ainda não está completamente pronto.
              O compartilhamento antecipado gera um link que será
              atualizado conforme os boletos forem ficando disponíveis.
            </div>

            <div class="card mb-3">
              <div class="card-header py-2 fw-semibold">
                Cobranças do lote
              </div>

              <div class="table-responsive lote-itens-table">
                <table class="table table-sm align-middle mb-0">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Status</th>
                      <th>Vencimento</th>
                      <th class="text-end">Valor</th>
                      <th class="text-center">Boleto</th>
                    </tr>
                  </thead>

                  <tbody>
                    <tr
                      v-for="(item, index) in itensDetalhe"
                      :key="item.cobranca_id || index"
                    >
                      <td>
                        {{ item.cobranca_id || '—' }}
                      </td>

                      <td>
                        {{ statusLabel(item.status) }}
                      </td>

                      <td>
                        {{ formatDate(dataVencimentoItem(item)) }}
                      </td>

                      <td class="text-end">
                        {{ formatMoney(valorItem(item)) }}
                      </td>

                      <td class="text-center">
                        <i
                          v-if="item.boleto_disponivel"
                          class="bi bi-check-circle-fill text-success"
                          title="Disponível"
                        ></i>

                        <span
                          v-else
                          class="text-muted"
                        >
                          —
                        </span>
                      </td>
                    </tr>

                    <tr v-if="itensDetalhe.length === 0">
                      <td
                        colspan="5"
                        class="text-center text-muted py-3"
                      >
                        Nenhuma cobrança encontrada no lote.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div
              v-if="podeOperar"
              class="card mb-3"
            >
              <div class="card-header py-2 fw-semibold">
                Distribuição
              </div>

              <div class="card-body">
                <p class="small text-muted mb-2">
                  {{
                    acaoAtual === 'ENVIO_BOLETOS'
                      ? 'O lote está pronto. Escolha os canais para enviar as cobranças.'
                      : 'O lote ainda não está pronto. Escolha os canais para compartilhar o acompanhamento antecipado.'
                  }}
                </p>

                <div class="d-flex flex-wrap gap-3 mb-3">
                  <label class="form-check">
                    <input
                      v-model="canalEmail"
                      type="checkbox"
                      class="form-check-input"
                      :disabled="operacaoEmAndamento"
                    />

                    <span class="form-check-label">
                      E-mail
                    </span>
                  </label>

                  <label class="form-check">
                    <input
                      v-model="canalWhatsapp"
                      type="checkbox"
                      class="form-check-input"
                      :disabled="operacaoEmAndamento"
                    />

                    <span class="form-check-label">
                      WhatsApp Web
                    </span>
                  </label>
                </div>

                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="
                    !acaoAtual ||
                    canaisSelecionados.length === 0 ||
                    operacaoEmAndamento
                  "
                  @click="distribuirLote"
                >
                  <span
                    v-if="distribuindo"
                    class="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  ></span>

                  <i
                    v-else
                    class="bi bi-send me-1"
                  ></i>

                  {{
                    distribuindo
                      ? 'Processando...'
                      : acaoAtual === 'ENVIO_BOLETOS'
                        ? 'Enviar cobranças'
                        : 'Compartilhar antecipadamente'
                  }}
                </button>
              </div>
            </div>

            <div
              v-if="ultimaDistribuicao"
              class="card border-success"
            >
              <div class="card-header py-2 fw-semibold">
                Resultado da distribuição
              </div>

              <div class="card-body">
                <div
                  v-for="resultado in ultimaDistribuicao.resultados || []"
                  :key="`${resultado.canal}-${resultado.auditoria_id || resultado.status}`"
                  class="distribuicao-resultado"
                >
                  <div>
                    <strong>
                      {{ canalLabel(resultado.canal) }}
                    </strong>

                    <div class="small text-muted">
                      Destinatário:
                      {{ resultado.destinatario || '—' }}
                    </div>

                    <div class="small">
                      Status:
                      <strong>
                        {{ distribuicaoStatusLabel(resultado.status) }}
                      </strong>
                    </div>
                  </div>

                  <button
                    v-if="
                      resultado.canal === 'WHATSAPP_WEB' &&
                      resultado.status === 'PREPARADO'
                    "
                    type="button"
                    class="btn btn-success btn-sm"
                    @click="abrirWhatsApp(resultado)"
                  >
                    <i class="bi bi-whatsapp me-1"></i>
                    Abrir WhatsApp Web
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <footer class="lote-modal-footer">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            :disabled="operacaoEmAndamento"
            @click="fecharLote"
          >
            Fechar
          </button>
        </footer>
      </section>
    </div>
  </div>
</template>

<script setup>
import { useBoletosOperacionais } from '@/composables/useBoletosOperacionais';
import { BFormSelect, BPagination } from 'bootstrap-vue-next';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';

import lotesCobrancasApi
  from '@/services/lotesCobrancas.api';

import {
  usePermissions
} from '@/composables/usePermissions';

const instance =
  getCurrentInstance();

const proxy =
  instance?.proxy;

const {
  fetchPermissions,
  hasPermission
} =
  usePermissions();

const loading = ref(false);
const carregandoDetalhe = ref(false);
const distribuindo = ref(false);

const lotes = ref([]);
const loteSelecionado = ref(null);
const itensDetalhe = ref([]);
const ultimaDistribuicao = ref(null);

const modalAberto = ref(false);
const errorMessage = ref('');
const actionErrorMessage = ref('');

const canalEmail = ref(false);
const canalWhatsapp = ref(false);

const paginaAtual = ref(1);
const limitePorPagina = ref(20);

const paginacao =
  reactive({
    pagina: 1,
    limite: 20,
    total_itens: 0,
    total_paginas: 0
  });

const filters =
  reactive({
    busca: '',
    origem_evento: ''
  });

const pageOptions = [10, 15, 20, 25, 50].map(value => ({ value, text: `${value} / página` }));
const startRow = computed(() => paginacao.total_itens === 0 ? 0 : (paginacao.pagina - 1) * paginacao.limite + 1);
const endRow = computed(() => Math.min(paginacao.pagina * paginacao.limite, paginacao.total_itens));

const podeVisualizar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_visualizar'
    )
  );

const boletosOperacionais = useBoletosOperacionais();
const podeOperar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_operar'
    ) && boletosOperacionais.value
  );

const operacaoEmAndamento =
  computed(() =>
    loading.value ||
    carregandoDetalhe.value ||
    distribuindo.value
  );

const acaoAtual =
  computed(() => {
    const readiness =
      readinessEstado(
        loteSelecionado.value
      );

    if (
      readiness ===
        'PRONTO_ENVIO'
    ) {
      return 'ENVIO_BOLETOS';
    }

    if (
      readiness ===
        'PARCIALMENTE_PRONTO' ||
      readiness ===
        'NAO_PRONTO'
    ) {
      return 'COMPARTILHAMENTO_ANTECIPADO';
    }

    return null;
  });

const canaisSelecionados =
  computed(() => {
    const canais = [];

    if (canalEmail.value) {
      canais.push(
        'EMAIL'
      );
    }

    if (canalWhatsapp.value) {
      canais.push(
        'WHATSAPP_WEB'
      );
    }

    return canais;
  });

let requestSequence = 0;
let searchTimer = null;

function apiErrorMessage(
  error,
  fallback
) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
}

async function mostrarAlerta(
  mensagem,
  titulo
) {
  if (proxy?.$alert) {
    await proxy.$alert(
      mensagem,
      titulo
    );

    return;
  }

  window.alert(
    `${titulo}: ${mensagem}`
  );
}

function numeroSeguro(value) {
  const number =
    Number(value);

  return Number.isFinite(number)
    ? number
    : 0;
}

function readinessObjeto(
  lote
) {
  const readiness =
    lote?.readiness;

  if (
    !readiness ||
    typeof readiness !==
      'object' ||
    Array.isArray(
      readiness
    )
  ) {
    return null;
  }

  return readiness;
}

function readinessEstado(
  lote
) {
  const value =
    readinessObjeto(
      lote
    )?.readiness;

  return [
    'PRONTO_ENVIO',
    'PARCIALMENTE_PRONTO',
    'NAO_PRONTO'
  ].includes(
    value
  )
    ? value
    : null;
}

function dataVencimentoItem(
  item
) {
  return (
    item?.data_vencimento_atual ||
    item?.data_vencimento_original ||
    null
  );
}

function valorItem(
  item
) {
  if (
    item?.valor_atual !==
      undefined &&
    item?.valor_atual !==
      null &&
    item?.valor_atual !==
      ''
  ) {
    return item.valor_atual;
  }

  return (
    item?.valor_original ??
    null
  );
}

function origemLabel(value) {
  return {
    NOVA_MATRICULA:
      'Nova matrícula',

    RENOVACAO_MATRICULA:
      'Renovação',

    TROCA_PLANO:
      'Troca de plano'
  }[value] || value || '—';
}

function readinessLabel(value) {
  return {
    PRONTO_ENVIO:
      'Pronto para envio',

    PARCIALMENTE_PRONTO:
      'Parcialmente pronto',

    NAO_PRONTO:
      'Não pronto'
  }[value] || value || '—';
}

function readinessClass(value) {
  if (value === 'PRONTO_ENVIO') {
    return 'lote-readiness-success';
  }

  if (
    value ===
      'PARCIALMENTE_PRONTO'
  ) {
    return 'lote-readiness-warning';
  }

  return 'lote-readiness-secondary';
}

function statusLabel(value) {
  return String(
    value || '—'
  )
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(
      /^./,
      (letter) =>
        letter.toUpperCase()
    );
}

function canalLabel(value) {
  if (value === 'WHATSAPP_WEB') {
    return 'WhatsApp Web';
  }

  if (value === 'EMAIL') {
    return 'E-mail';
  }

  return value || '—';
}

function distribuicaoStatusLabel(
  value
) {
  return {
    PREPARADO:
      'Preparado',

    ENVIADO:
      'Enviado',

    ERRO:
      'Erro'
  }[value] || value || '—';
}

function formatMoney(value) {
  const number =
    Number(value ?? 0);

  if (!Number.isFinite(number)) {
    return '—';
  }

  return new Intl.NumberFormat(
    'pt-BR',
    {
      style: 'currency',
      currency: 'BRL'
    }
  ).format(
    number
  );
}

function formatDate(value) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(
      `${String(value).slice(0, 10)}T12:00:00`
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }

  return new Intl.DateTimeFormat(
    'pt-BR'
  ).format(
    date
  );
}

function formatDateTime(value) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }

  return new Intl.DateTimeFormat(
    'pt-BR',
    {
      dateStyle: 'short',
      timeStyle: 'short'
    }
  ).format(
    date
  );
}

function normalizeDetail(
  result
) {
  const data =
    result?.data ??
    result;

  const lote =
    data?.lote ??
    data;

  const itens =
    Array.isArray(
      data?.itens
    )
      ? data.itens
      : Array.isArray(
          lote?.itens
        )
        ? lote.itens
        : [];

  return {
    lote,
    itens
  };
}

async function carregarLotes() {
  if (!podeVisualizar.value) {
    return;
  }

  const requestId =
    ++requestSequence;

  loading.value = true;
  errorMessage.value = '';

  try {
    const result =
      await lotesCobrancasApi
        .listar({
          pagina:
            paginaAtual.value,

          limite:
            limitePorPagina.value,

          busca:
            filters.busca,

          origem_evento:
            filters.origem_evento
        });

    if (
      requestId !==
      requestSequence
    ) {
      return;
    }

    lotes.value =
      result.data;

    Object.assign(
      paginacao,
      result.paginacao
    );

    if (
      paginaAtual.value >
        Math.max(
          result.paginacao
            .total_paginas,
          1
        )
    ) {
      paginaAtual.value =
        Math.max(
          result.paginacao
            .total_paginas,
          1
        );
    }
  } catch (error) {
    if (
      requestId !==
      requestSequence
    ) {
      return;
    }

    console.error(
      'Erro ao carregar lotes de cobranças:',
      error
    );

    lotes.value = [];

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar os lotes de cobranças.'
      );
  } finally {
    if (
      requestId ===
      requestSequence
    ) {
      loading.value = false;
    }
  }
}

function executarNaPrimeiraPagina() {
  if (paginaAtual.value !== 1) {
    paginaAtual.value = 1;
    return;
  }

  carregarLotes();
}

function aplicarFiltros() {
  executarNaPrimeiraPagina();
}

function limparFiltros() {
  Object.assign(
    filters,
    {
      busca: '',
      origem_evento: ''
    }
  );

  executarNaPrimeiraPagina();
}

function limparDistribuicaoEmMemoria() {
  ultimaDistribuicao.value = null;
  actionErrorMessage.value = '';
  canalEmail.value = false;
  canalWhatsapp.value = false;
}

async function abrirLote(
  lote
) {
  if (
    operacaoEmAndamento.value ||
    !lote?.id
  ) {
    return;
  }

  modalAberto.value = true;

  loteSelecionado.value =
    lote;

  itensDetalhe.value = [];

  limparDistribuicaoEmMemoria();

  carregandoDetalhe.value = true;

  try {
    const result =
      await lotesCobrancasApi
        .detalhar(
          lote.id
        );

    const normalized =
      normalizeDetail(
        result
      );

    if (!normalized.lote?.id) {
      throw new Error(
        'O detalhe do lote retornado pelo servidor está incompleto.'
      );
    }

    loteSelecionado.value =
      normalized.lote;

    itensDetalhe.value =
      normalized.itens;
  } catch (error) {
    console.error(
      'Erro ao carregar lote de cobranças:',
      error
    );

    actionErrorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar o lote.'
      );
  } finally {
    carregandoDetalhe.value = false;
  }
}

function fecharLote() {
  if (operacaoEmAndamento.value) {
    return;
  }

  modalAberto.value = false;
  loteSelecionado.value = null;
  itensDetalhe.value = [];

  limparDistribuicaoEmMemoria();
}

async function distribuirLote() {
  if (
    !podeOperar.value ||
    distribuindo.value ||
    !loteSelecionado.value?.id ||
    !acaoAtual.value ||
    canaisSelecionados.value
      .length === 0
  ) {
    return;
  }

  const canais =
    [
      ...canaisSelecionados.value
    ];

  const descricaoAcao =
    acaoAtual.value ===
      'ENVIO_BOLETOS'
      ? 'enviar as cobranças'
      : 'compartilhar antecipadamente o lote';

  const confirmou =
    window.confirm(
      `Deseja ${descricaoAcao} pelos canais selecionados?`
    );

  if (!confirmou) {
    return;
  }

  distribuindo.value = true;
  actionErrorMessage.value = '';
  ultimaDistribuicao.value = null;

  try {
    const result =
      await lotesCobrancasApi
        .distribuir(
          loteSelecionado.value.id,
          {
            acao:
              acaoAtual.value,

            canais
          }
        );

    if (
      !Array.isArray(
        result?.resultados
      )
    ) {
      throw new Error(
        'O resultado da distribuição retornado pelo servidor está incompleto.'
      );
    }

    /*
     * A credencial pública eventualmente presente na resposta
     * permanece somente neste ref em memória. Ela não é gravada
     * em storage, rota, query string ou estado persistente.
     */
    ultimaDistribuicao.value =
      result;

    const erros =
      result.resultados.filter(
        (item) =>
          item.status ===
            'ERRO'
      );

    if (erros.length) {
      await mostrarAlerta(
        'A distribuição foi processada, mas um ou mais canais apresentaram erro.',
        'Distribuição concluída com alertas'
      );
    }
  } catch (error) {
    console.error(
      'Erro ao distribuir lote de cobranças:',
      error
    );

    actionErrorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível distribuir o lote.'
      );
  } finally {
    distribuindo.value = false;
  }
}

async function abrirWhatsApp(
  resultado
) {
  if (
    resultado?.canal !==
      'WHATSAPP_WEB' ||
    resultado?.status !==
      'PREPARADO'
  ) {
    return;
  }

  const telefone =
    String(
      resultado.destinatario ||
      ''
    )
      .replace(
        /\D/g,
        ''
      );

  const mensagem =
    String(
      resultado.mensagem ||
      ''
    ).trim();

  if (
    !telefone ||
    !mensagem
  ) {
    await mostrarAlerta(
      'O servidor não retornou telefone e mensagem válidos para o WhatsApp.',
      'WhatsApp'
    );

    return;
  }

  const url =
    new URL(
      'https://web.whatsapp.com/send'
    );

  url.searchParams.set(
    'phone',
    telefone
  );

  url.searchParams.set(
    'text',
    mensagem
  );

  const popup =
    window.open(
      url.toString(),
      '_blank',
      'noopener,noreferrer'
    );

  if (popup) {
    popup.opener = null;
  }
}

watch(limitePorPagina, executarNaPrimeiraPagina);

watch(
  paginaAtual,
  () => {
    carregarLotes();
  }
);

watch(
  () => filters.busca,
  () => {
    window.clearTimeout(
      searchTimer
    );

    searchTimer =
      window.setTimeout(
        aplicarFiltros,
        400
      );
  }
);

onMounted(
  async () => {
    await fetchPermissions();

    if (!podeVisualizar.value) {
      errorMessage.value =
        'Você não possui permissão para visualizar cobranças.';

      return;
    }

    await carregarLotes();
  }
);

onBeforeUnmount(
  () => {
    window.clearTimeout(
      searchTimer
    );

    requestSequence += 1;

    /*
     * Remove também da memória qualquer resposta que contenha
     * a URL pública usada na última operação de distribuição.
     */
    ultimaDistribuicao.value = null;
  }
);
</script>

<style scoped>
.page-limit { width: 6rem; }
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

.lotes-cobrancas {
  min-width: 0;
}

.lotes-filtros {
  display: grid;
  grid-template-columns:
    minmax(260px, 2fr)
    minmax(180px, 1fr)
    auto;
  gap: 0.75rem;
  align-items: end;
}

.lotes-filtro-busca {
  min-width: 0;
}

.form-label {
  margin-bottom: 0.25rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #495057;
}

.lote-readiness {
  display: inline-flex;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  white-space: nowrap;
}

.lote-readiness-success {
  color: #146c43;
  background: #d1e7dd;
}

.lote-readiness-warning {
  color: #664d03;
  background: #fff3cd;
}

.lote-readiness-secondary {
  color: #41464b;
  background: #e2e3e5;
}

.lote-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.45);
}

.lote-modal {
  display: flex;
  flex-direction: column;
  width: min(980px, 100%);
  max-height: calc(100vh - 2rem);
  overflow: hidden;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.2);
}

.lote-modal-header,
.lote-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.9rem 1rem;
}

.lote-modal-header {
  border-bottom: 1px solid #dee2e6;
}

.lote-modal-footer {
  justify-content: flex-end;
  border-top: 1px solid #dee2e6;
}

.lote-modal-body {
  overflow-y: auto;
  padding: 1rem;
}

.lote-resumo-grid {
  display: grid;
  grid-template-columns:
    repeat(4, minmax(130px, 1fr));
  gap: 0.75rem;
}

.lote-resumo-grid strong {
  overflow-wrap: anywhere;
}

.lote-resumo-grid > div {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.65rem;
  border: 1px solid #dee2e6;
  border-radius: 0.4rem;
}

.lote-resumo-grid span {
  color: #6c757d;
  font-size: 0.72rem;
}

.lote-itens-table {
  max-height: 280px;
}

.distribuicao-resultado {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e9ecef;
}

.distribuicao-resultado:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

@media (max-width: 991.98px) {
  .lotes-filtros {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767.98px) {

  .lote-resumo-grid {
    grid-template-columns:
      repeat(2, minmax(0, 1fr));
  }

  .distribuicao-resultado {
    align-items: flex-start;
    flex-direction: column;
  }
}

@media (max-width: 479.98px) {
  .lote-resumo-grid {
    grid-template-columns: 1fr;
  }
}
</style>
